import React, { useState, useEffect, useCallback, useRef } from 'react';
import './SingBoxGuide.css';
import { useTranslation } from './i18n';

/**
 * Visual step-by-step guide for sing-box VPN setup
 * Uses real iOS screenshots for better UX
 */

const platforms = {
  ios: {
    name: 'iOS / iPhone / iPad',
    icon: '📱',
    downloadUrl: 'https://apps.apple.com/app/sing-box-mt/id6785326793',
    steps: [
      {
        id: 'install-app',
        title: 'Скачайте sing-box',
        description: 'Установите приложение Sing-Box VT из App Store',
        image: '/guide-images/ios/step-1-appstore.webp',
        imageAlt: 'Страница sing-box VT в App Store',
        details: 'Приложение бесплатное, рейтинг 4.4★. Разработчик: VIRAL TECH, INC.',
        action: {
          type: 'link',
          label: '📥 Открыть App Store',
          url: 'https://apps.apple.com/app/sing-box-mt/id6785326793'
        }
      },
      {
        id: 'add-subscription',
        title: 'Добавьте подписку',
        description: 'В мини-приложении нажмите "Добавить подписку в sing-box"',
        image: '/guide-images/ios/step-2-miniapp-dialog.webp',
        imageAlt: 'Диалог добавления VPN подписки',
        details: 'Появится диалог подтверждения. Нажмите "Yes" для продолжения.',
        action: {
          type: 'callback',
          label: '🌍 Добавить подписку',
          callbackId: 'addToSingBox'
        }
      },
      {
        id: 'import-profile',
        title: 'Подтвердите импорт',
        description: 'В sing-box нажмите "Import" в диалоговом окне',
        image: '/guide-images/ios/step-3-singbox-import.webp',
        imageAlt: 'Диалог Import Remote Profile в sing-box',
        details: 'Профиль будет загружен с сервера vpn-admin.shurik.ch'
      },
      {
        id: 'create-profile',
        title: 'Нажмите Create',
        description: 'На экране настроек профиля нажмите кнопку "Create"',
        image: '/guide-images/ios/step-6-profile-settings.webp',
        imageAlt: 'Экран создания профиля с кнопкой Create',
        details: 'Убедитесь что Auto Update включен. Нажмите синюю кнопку "Create" внизу.'
      },
      {
        id: 'select-profile',
        title: 'Выберите профиль',
        description: 'Перейдите в Profiles и выберите добавленный профиль',
        image: '/guide-images/ios/step-5-profiles-list.webp',
        imageAlt: 'Список профилей в sing-box с VPN Shurik',
        details: 'Профиль "VPN Shurik" должен появиться в списке. Нажмите на него.'
      },
      {
        id: 'install-extension',
        title: 'Установите Network Extension',
        description: 'На Dashboard нажмите "Install Network Extension"',
        image: '/guide-images/ios/step-4-install-extension.webp',
        imageAlt: 'Dashboard sing-box с кнопкой Install Network Extension',
        details: 'iOS запросит разрешение на добавление VPN. Подтвердите в настройках.'
      },
      {
        id: 'connected',
        title: 'Готово! VPN подключен',
        description: 'VPN успешно настроен и готов к использованию',
        image: '/guide-images/ios/step-7-vpn-connected.webp',
        imageAlt: 'iOS Settings показывает VPN Connected',
        details: 'В статус-баре iOS появится значок VPN. Весь трафик теперь защищён.'
      }
    ]
  },
  android: {
    name: 'Android',
    icon: '🤖',
    downloadUrl: 'https://play.google.com/store/apps/details?id=io.nekohasekai.sfa',
    steps: [
      {
        id: 'install-app',
        titleKey: 'guide.steps.android.install.title',
        descriptionKey: 'guide.steps.android.install.description',
        image: '/guide-images/android/step-1-playstore.webp',
        imageAlt: 'Google Play Store страница приложения sing-box',
        detailsKey: 'guide.steps.android.install.details',
        action: {
          type: 'link',
          labelKey: 'guide.steps.android.install.button',
          url: 'https://play.google.com/store/apps/details?id=io.nekohasekai.sfa'
        }
      },
      {
        id: 'add-subscription',
        titleKey: 'guide.steps.android.addSubscription.title',
        descriptionKey: 'guide.steps.android.addSubscription.description',
        image: '/guide-images/android/step-2-miniapp-dialog.webp',
        imageAlt: 'Диалог добавления VPN подписки в мини-приложении',
        detailsKey: 'guide.steps.android.addSubscription.details',
        action: {
          type: 'callback',
          labelKey: 'guide.steps.android.addSubscription.button',
          callbackId: 'addToSingBox'
        }
      },
      {
        id: 'select-profile',
        titleKey: 'guide.steps.android.selectProfile.title',
        descriptionKey: 'guide.steps.android.selectProfile.description',
        image: '/guide-images/android/step-3-profiles-list.webp',
        imageAlt: 'Список профилей в sing-box с VPN Shurik',
        detailsKey: 'guide.steps.android.selectProfile.details'
      },
      {
        id: 'start-vpn',
        titleKey: 'guide.steps.android.startVpn.title',
        descriptionKey: 'guide.steps.android.startVpn.description',
        image: '/guide-images/android/step-4-start-vpn.webp',
        imageAlt: 'Экран профиля с кнопкой Start',
        detailsKey: 'guide.steps.android.startVpn.details'
      },
      {
        id: 'vpn-connected',
        titleKey: 'guide.steps.android.vpnConnected.title',
        descriptionKey: 'guide.steps.android.vpnConnected.description',
        image: '/guide-images/android/step-5-vpn-connected.webp',
        imageAlt: 'Экран с активным VPN подключением',
        detailsKey: 'guide.steps.android.vpnConnected.details'
      }
    ]
  }
};

const SingBoxGuide = ({ onClose, onAddSubscription }) => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState('ios');
  const [currentStep, setCurrentStep] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [slideDirection, setSlideDirection] = useState(null);

  // Swipe gesture state
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const minSwipeDistance = 50; // Minimum swipe distance in pixels

  const currentPlatform = platforms[activeTab];
  const totalSteps = currentPlatform.steps.length;
  const step = currentPlatform.steps[currentStep];

  // Reset step when changing platform
  useEffect(() => {
    setCurrentStep(0);
    setImageLoaded(false);
  }, [activeTab]);

  // Clear slide animation after it completes
  useEffect(() => {
    if (slideDirection) {
      const timer = setTimeout(() => setSlideDirection(null), 300);
      return () => clearTimeout(timer);
    }
  }, [slideDirection]);

  const goToStep = useCallback((index) => {
    if (index >= 0 && index < totalSteps && index !== currentStep) {
      setSlideDirection(index > currentStep ? 'left' : 'right');
      setCurrentStep(index);
      setImageLoaded(false);
      setImageError(false);
    }
  }, [totalSteps, currentStep]);

  const nextStep = useCallback(() => goToStep(currentStep + 1), [goToStep, currentStep]);
  const prevStep = useCallback(() => goToStep(currentStep - 1), [goToStep, currentStep]);

  // Swipe gesture handlers
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  }, []);

  const handleTouchMove = useCallback((e) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const isSwipe = Math.abs(distance) > minSwipeDistance;

    if (isSwipe) {
      if (distance > 0) {
        // Swipe left → next step
        nextStep();
      } else {
        // Swipe right → previous step
        prevStep();
      }
    }

    // Reset
    touchStartX.current = null;
    touchEndX.current = null;
  }, [nextStep, prevStep]);

  // Keyboard navigation (desktop only, skip in Telegram Mini App)
  useEffect(() => {
    // Skip keyboard listeners in Telegram Mini App (mobile context)
    if (window.Telegram?.WebApp) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') nextStep();
      else if (e.key === 'ArrowLeft') prevStep();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextStep, prevStep, onClose]);

  const handleAction = (action) => {
    if (!action) return;

    if (action.type === 'link' && action.url) {
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.openLink(action.url);
      } else {
        window.open(action.url, '_blank');
      }
    } else if (action.type === 'callback' && action.callbackId === 'addToSingBox') {
      if (onAddSubscription) {
        onAddSubscription('subscription');
      }
    }
  };

  return (
    <div className="sing-box-guide-overlay" onClick={onClose}>
      <div className="sing-box-guide-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="guide-header">
          <h2>📱 {t('guide.title')}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Platform Tabs */}
        <div className="platform-tabs">
          {Object.keys(platforms).map(platform => (
            <button
              key={platform}
              className={`tab-btn ${activeTab === platform ? 'active' : ''}`}
              onClick={() => setActiveTab(platform)}
            >
              <span className="tab-icon">{platforms[platform].icon}</span>
              {platforms[platform].name}
            </button>
          ))}
        </div>

        {/* Step Content - with swipe gestures */}
        <div
          className={`guide-content ${slideDirection ? `slide-${slideDirection}` : ''}`}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Image Section */}
          <div className="step-image-container">
            {step.image ? (
              <>
                {!imageLoaded && !imageError && (
                  <div className="image-placeholder">
                    <div className="loading-spinner"></div>
                  </div>
                )}
                {imageError ? (
                  <div className="image-placeholder error">
                    <span className="placeholder-icon">⚠️</span>
                    <span className="placeholder-text">{t('common.error')}</span>
                  </div>
                ) : (
                  <img
                    src={step.image}
                    alt={step.imageAlt}
                    className={`step-image ${imageLoaded ? 'loaded' : 'loading'}`}
                    onLoad={() => setImageLoaded(true)}
                    onError={() => {
                      setImageLoaded(true);
                      setImageError(true);
                    }}
                    loading="lazy"
                  />
                )}
              </>
            ) : (
              <div className="image-placeholder no-image">
                <span className="placeholder-icon">📱</span>
                <span className="placeholder-text">Скриншоты скоро</span>
              </div>
            )}
          </div>

          {/* Step Info */}
          <div className="step-info">
            <div className="step-number-badge">
              {t('guide.stepCounter', { current: currentStep + 1, total: totalSteps })}
            </div>
            <h3 className="step-title">{step.titleKey ? t(step.titleKey) : step.title}</h3>
            <p className="step-description">{step.descriptionKey ? t(step.descriptionKey) : step.description}</p>
            {step.detailsKey ? (
              <p className="step-details">{t(step.detailsKey)}</p>
            ) : step.details ? (
              <p className="step-details">{step.details}</p>
            ) : null}
          </div>

          {/* Action Button */}
          {step.action && (
            <button
              className="step-action-btn"
              onClick={() => handleAction(step.action)}
            >
              {step.action.labelKey ? t(step.action.labelKey) : step.action.label}
            </button>
          )}
        </div>

        {/* Navigation */}
        <div className="guide-navigation">
          <button
            className="nav-btn prev"
            onClick={prevStep}
            disabled={currentStep === 0}
            aria-label="Предыдущий шаг"
          >
            ◄
          </button>

          {/* Step Indicators (dots) */}
          <div className="step-indicators">
            {currentPlatform.steps.map((_, index) => (
              <button
                key={index}
                className={`step-dot ${index === currentStep ? 'active' : ''}`}
                onClick={() => goToStep(index)}
                aria-label={`Шаг ${index + 1}`}
              />
            ))}
          </div>

          <button
            className="nav-btn next"
            onClick={nextStep}
            disabled={currentStep === totalSteps - 1}
            aria-label="Следующий шаг"
          >
            ►
          </button>
        </div>
      </div>
    </div>
  );
};

export default SingBoxGuide;
